/**
 * podcast.js
 *
 * Handles the "Download guide" flow for the podcast page:
 *   1. On click, decide whether to prompt for newsletter signup or just download.
 *   2. Show a modal (cloned from the <template> in the section) when prompting.
 *   3. Submit signups directly to Klaviyo's client subscription endpoint.
 *   4. Trigger the PDF download whether the user subscribes or skips.
 *
 * State lives in localStorage under one key. Two fields are read by the gate:
 *   subscribed:    boolean — set to true after a successful Klaviyo signup
 *   lastPromptAt:  ISO timestamp — updated every time the modal is dismissed,
 *                  used to suppress re-prompting for `cooldownDays` days.
 *
 * Config is pulled from data-* attributes on .podcast-episodes:
 *   data-klaviyo-public-key, data-klaviyo-list-id, data-prompt-cooldown-days
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'lsk_podcast_newsletter';
  const DEFAULT_COOLDOWN_DAYS = 7;
  const KLAVIYO_REVISION = '2024-10-15';
  const MS_PER_DAY = 86400000;

  const config = {
    publicKey: null,
    listId: null,
    cooldownDays: DEFAULT_COOLDOWN_DAYS,
  };

  // ---------- State ----------

  function getState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function setState(updates) {
    const next = Object.assign({}, getState(), updates);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage may be unavailable (private mode, quota). Fail silently;
      // the user just gets prompted again next visit.
    }
    return next;
  }

  function isSubscribed() {
    return getState().subscribed === true;
  }

  function isInCooldown() {
    const { lastPromptAt } = getState();
    if (!lastPromptAt) return false;
    const ts = new Date(lastPromptAt).getTime();
    if (isNaN(ts)) return false;
    return Date.now() - ts < config.cooldownDays * MS_PER_DAY;
  }

  // ---------- Download ----------

  function triggerDownload(url, filename) {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    if (filename) a.download = filename;
    a.rel = 'noopener';
    // target=_blank acts as a fallback when the `download` attribute is
    // ignored due to cross-origin (cdn.shopify.com vs storefront domain).
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // ---------- Klaviyo ----------

  async function subscribeToKlaviyo(email, customProps) {
    if (!config.publicKey || !config.listId) {
      console.warn('[podcast] Klaviyo public key or list ID missing.');
      return { ok: false, error: 'not_configured' };
    }
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Podcast guide download',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: customProps || {},
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: config.listId } },
        },
      },
    };
    try {
      const url =
        'https://a.klaviyo.com/client/subscriptions/?company_id=' +
        encodeURIComponent(config.publicKey);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });
      // Klaviyo returns 202 Accepted with empty body on success.
      if (res.status === 202 || res.ok) {
        return { ok: true };
      }
      let detail = 'HTTP ' + res.status;
      try {
        const data = await res.json();
        if (data && data.errors && data.errors[0] && data.errors[0].detail) {
          detail = data.errors[0].detail;
        }
      } catch (e) {
        /* swallow */
      }
      return { ok: false, error: detail };
    } catch (e) {
      return { ok: false, error: e.message || 'network_error' };
    }
  }

  /**
   * Fire a behavioral event in Klaviyo. Uses the Onsite snippet (_learnq)
   * if present, since it knows the currently identified profile. If the
   * snippet isn't on the page, this is a no-op — sending anonymous events
   * via the client API requires an identified profile, which we don't
   * have for non-subscribers.
   */
  function trackKlaviyoEvent(eventName, properties) {
    if (window._learnq && typeof window._learnq.push === 'function') {
      try {
        window._learnq.push(['track', eventName, properties || {}]);
      } catch (e) {
        /* swallow */
      }
    }
  }

  // ---------- Modal ----------

  let modalEl = null;
  let modalContext = null;
  let lastFocusedEl = null;

  function ensureModal() {
    if (modalEl) return modalEl;
    const tpl = document.getElementById('podcast-newsletter-modal-template');
    if (!tpl || !tpl.content) {
      console.error('[podcast] Modal template not found.');
      return null;
    }
    const frag = tpl.content.cloneNode(true);
    modalEl = frag.querySelector('.podcast-modal');
    if (!modalEl) return null;
    document.body.appendChild(modalEl);
    bindModal(modalEl);
    return modalEl;
  }

  function bindModal(el) {
    const titleEl = el.querySelector('.podcast-modal__title');
    if (titleEl) titleEl.dataset.originalTitle = titleEl.textContent;

    el.addEventListener('click', function (e) {
      const t = e.target;
      if (!t.closest) return;
      if (t.closest('[data-modal-dismiss]')) {
        closeModal({ download: false });
      } else if (t.closest('[data-modal-skip]')) {
        closeModal({ download: true, submitted: false });
      } else if (t.closest('[data-modal-download-confirm]')) {
        closeModal({ download: true, submitted: true });
      }
    });

    const form = el.querySelector('[data-modal-form]');
    if (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        const email = (input && input.value || '').trim();
        if (!email) {
          if (input) input.focus();
          return;
        }
        const errEl = el.querySelector('[data-modal-error]');
        if (errEl) {
          errEl.hidden = true;
          errEl.textContent = '';
        }
        const submitBtn = form.querySelector('button[type="submit"]');
        const originalText = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Sending…';
        }

        const result = await subscribeToKlaviyo(email, {
          source: 'podcast_guide_download',
          last_guide_episode: modalContext ? modalContext.episodeTitle : null,
          last_guide_episode_number: modalContext ? modalContext.episodeNumber : null,
        });

        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }

        if (result.ok) {
          setState({
            subscribed: true,
            email: email,
            lastPromptAt: new Date().toISOString(),
          });
          document.body.classList.add('podcast-newsletter-subscribed');
          showConfirmation(el);
        } else if (errEl) {
          errEl.textContent =
            'Something went wrong saving your email. The download will still work — try again or use "Just download" below.';
          errEl.hidden = false;
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        closeModal({ download: false });
      } else if (e.key === 'Tab') {
        trapFocus(e, el.querySelector('.podcast-modal__panel'));
      }
    });
  }

  function openModal(ctx) {
    const el = ensureModal();
    if (!el) {
      // Template missing — degrade to just downloading.
      triggerDownload(ctx.pdfUrl, ctx.filename);
      return;
    }
    modalContext = ctx;
    lastFocusedEl = document.activeElement;
    el.hidden = false;
    el.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';

    const input = el.querySelector('input[type="email"]');
    if (input) {
      input.value = '';
      setTimeout(function () {
        input.focus();
      }, 50);
    }
    const errEl = el.querySelector('[data-modal-error]');
    if (errEl) {
      errEl.hidden = true;
      errEl.textContent = '';
    }

    const titleEl = el.querySelector('.podcast-modal__title');
    const subheadEl = el.querySelector('.podcast-modal__subhead');
    const confirmEl = el.querySelector('[data-modal-confirmation]');
    const skipBtn = el.querySelector('[data-modal-skip]');
    const legalEl = el.querySelector('.podcast-modal__legal');
    const form = el.querySelector('[data-modal-form]');
    if (titleEl) titleEl.textContent = titleEl.dataset.originalTitle || titleEl.textContent;
    if (subheadEl) subheadEl.hidden = false;
    if (confirmEl) confirmEl.hidden = true;
    if (form) form.hidden = false;
    if (skipBtn) skipBtn.hidden = false;
    if (legalEl) legalEl.hidden = false;
  }

  function closeModal(opts) {
    if (!modalEl) return;
    const download = !!(opts && opts.download);
    const submitted = !!(opts && opts.submitted);

    modalEl.classList.remove('is-open');
    modalEl.hidden = true;
    document.documentElement.style.overflow = '';

    if (download && submitted) {
      setState({ lastPromptAt: new Date().toISOString() });
    }

    if (download && modalContext) {
      triggerDownload(modalContext.pdfUrl, modalContext.filename);
      trackKlaviyoEvent('Downloaded Podcast Guide', {
        episode_number: modalContext.episodeNumber,
        episode_title: modalContext.episodeTitle,
        source: submitted ? 'modal_submit' : 'modal_skip',
      });
    }

    modalContext = null;
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  function showConfirmation(el) {
    const titleEl = el.querySelector('.podcast-modal__title');
    const subheadEl = el.querySelector('.podcast-modal__subhead');
    const form = el.querySelector('[data-modal-form]');
    const skipBtn = el.querySelector('[data-modal-skip]');
    const legalEl = el.querySelector('.podcast-modal__legal');
    const confirmEl = el.querySelector('[data-modal-confirmation]');
    if (titleEl) titleEl.textContent = 'Thank you!';
    if (subheadEl) subheadEl.hidden = true;
    if (form) form.hidden = true;
    if (skipBtn) skipBtn.hidden = true;
    if (legalEl) legalEl.hidden = true;
    if (confirmEl) {
      confirmEl.hidden = false;
      const downloadBtn = confirmEl.querySelector('[data-modal-download-confirm]');
      if (downloadBtn) setTimeout(function () { downloadBtn.focus(); }, 50);
    }
  }

  function trapFocus(e, container) {
    if (!container) return;
    const focusable = Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (node) {
      let n = node;
      while (n && n !== container) {
        if (n.hidden) return false;
        n = n.parentElement;
      }
      return true;
    });
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ---------- Sticky audio player ----------

  const player = {
    el: null,
    audio: null,
    toggleBtn: null,
    closeBtn: null,
    seek: null,
    currentTimeEl: null,
    durationEl: null,
    titleEl: null,
    episodeNumberEl: null,
    activeButton: null,
    audioUrl: null,
  };

  function formatTime(sec) {
    if (!isFinite(sec) || sec < 0) return '0:00';
    const minutes = Math.floor(sec / 60);
    const seconds = String(Math.floor(sec % 60)).padStart(2, '0');
    return minutes + ':' + seconds;
  }

  function ensurePlayer() {
    if (player.el) return player.el;

    player.el = document.querySelector('[data-podcast-player]');
    if (!player.el) return null;

    player.audio = player.el.querySelector('[data-player-audio]');
    player.toggleBtn = player.el.querySelector('[data-player-toggle]');
    player.closeBtn = player.el.querySelector('[data-player-close]');
    player.seek = player.el.querySelector('[data-player-seek]');
    player.currentTimeEl = player.el.querySelector('[data-player-current-time]');
    player.durationEl = player.el.querySelector('[data-player-duration]');
    player.titleEl = player.el.querySelector('[data-player-title]');
    player.episodeNumberEl = player.el.querySelector('[data-player-episode-number]');

    bindPlayer();
    return player.el;
  }

  function bindPlayer() {
    if (!player.audio) return;

    if (player.toggleBtn) {
      player.toggleBtn.addEventListener('click', function () {
        if (player.audio.paused) {
          player.audio.play().catch(function (err) {
            console.error('[podcast] Playback failed to start:', err);
          });
        } else {
          player.audio.pause();
        }
      });
    }

    if (player.closeBtn) {
      player.closeBtn.addEventListener('click', closePlayer);
    }

    if (player.seek) {
      player.seek.addEventListener('input', function () {
        const nextTime = parseFloat(player.seek.value);
        if (!isNaN(nextTime)) player.audio.currentTime = nextTime;
      });
    }

    player.audio.addEventListener('loadedmetadata', syncDuration);
    player.audio.addEventListener('durationchange', syncDuration);
    player.audio.addEventListener('timeupdate', syncCurrentTime);
    player.audio.addEventListener('play', function () {
      setPlayerPlaybackState(true);
    });
    player.audio.addEventListener('pause', function () {
      setPlayerPlaybackState(false);
    });
    player.audio.addEventListener('ended', function () {
      setPlayerPlaybackState(false);
    });
  }

  function syncDuration() {
    if (!player.audio) return;
    const duration = isFinite(player.audio.duration) ? player.audio.duration : 0;
    if (player.durationEl) player.durationEl.textContent = formatTime(duration);
    if (player.seek) player.seek.max = duration || 100;
  }

  function syncCurrentTime() {
    if (!player.audio) return;
    if (player.currentTimeEl) player.currentTimeEl.textContent = formatTime(player.audio.currentTime);
    if (player.seek) player.seek.value = player.audio.currentTime || 0;
  }

  function setPlayerPlaybackState(isPlaying) {
    if (!player.el) return;
    player.el.classList.toggle('is-playing', isPlaying);
    player.el.classList.toggle('is-paused', !isPlaying);

    if (player.toggleBtn) {
      player.toggleBtn.setAttribute('aria-label', isPlaying ? 'Pause episode' : 'Play episode');
    }

    if (player.activeButton) {
      player.activeButton.classList.toggle('is-playing', isPlaying);
      player.activeButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    }
  }

  function setActiveEpisodeButton(button) {
    if (player.activeButton && player.activeButton !== button) {
      player.activeButton.classList.remove('is-playing');
      player.activeButton.setAttribute('aria-pressed', 'false');
    }
    player.activeButton = button;
    if (player.activeButton) player.activeButton.setAttribute('aria-pressed', 'true');
  }

  function openPlayer(ctx, button) {
    const el = ensurePlayer();
    if (!el || !player.audio || !ctx.audioUrl) return;

    const isCurrentAudio = player.audioUrl === ctx.audioUrl;
    const isPlayerVisible = !el.hidden;

    setActiveEpisodeButton(button);

    if (isCurrentAudio && isPlayerVisible && !player.audio.paused) {
      player.audio.pause();
      return;
    }

    if (!isCurrentAudio) {
      player.audioUrl = ctx.audioUrl;
      player.audio.src = ctx.audioUrl;
      player.audio.currentTime = 0;
      if (player.seek) player.seek.value = 0;
      if (player.currentTimeEl) player.currentTimeEl.textContent = '0:00';
      if (player.durationEl) player.durationEl.textContent = '0:00';
    }

    if (player.titleEl) player.titleEl.textContent = ctx.episodeTitle || 'Podcast episode';
    if (player.episodeNumberEl) {
      player.episodeNumberEl.textContent = ctx.episodeNumber ? 'Episode ' + ctx.episodeNumber : '';
    }

    el.hidden = false;
    document.body.classList.add('podcast-player-active');
    setPlayerPlaybackState(false);

    player.audio.play().then(function () {
      trackKlaviyoEvent('Played Podcast Episode', {
        episode_number: ctx.episodeNumber,
        episode_title: ctx.episodeTitle,
      });
    }).catch(function (err) {
      console.error('[podcast] Playback failed to start:', err);
      setPlayerPlaybackState(false);
    });
  }

  function closePlayer() {
    ensurePlayer();
    if (!player.el || !player.audio) return;

    player.audio.pause();
    player.el.hidden = true;
    document.body.classList.remove('podcast-player-active');

    if (player.activeButton) {
      player.activeButton.classList.remove('is-playing');
      player.activeButton.setAttribute('aria-pressed', 'false');
    }
  }

  // ---------- Episode details ----------

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cleanupDetailsAnimation(details, panel) {
    details.classList.remove('is-animating');
    panel.style.height = '';
    panel.style.opacity = '';
  }

  function animateEpisodeDetails(details, shouldOpen) {
    const panel = details.querySelector('.podcast-episode__details-panel');
    const summary = details.querySelector('.podcast-episode__summary');

    if (!panel || !shouldOpen || prefersReducedMotion()) {
      details.open = shouldOpen;
      if (summary) summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      return;
    }

    details.classList.add('is-animating');

    details.open = true;
    panel.style.height = '0px';
    panel.style.opacity = '0';

    requestAnimationFrame(function () {
      panel.style.height = panel.scrollHeight + 'px';
      panel.style.opacity = '1';
    });

    if (summary) summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    let didFinish = false;
    function finishAnimation() {
      if (didFinish) return;
      didFinish = true;
      cleanupDetailsAnimation(details, panel);
    }

    panel.addEventListener('transitionend', function onTransitionEnd(e) {
      if (e.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishAnimation();
    });
    setTimeout(finishAnimation, 350);
  }

  // ---------- Click handler ----------

  function handleEpisodeDetailsClick(e) {
    const summary = e.target.closest && e.target.closest('.podcast-episode__summary');
    if (!summary) return;

    const details = summary.closest('.podcast-episode__details');
    if (!details) return;

    e.preventDefault();
    if (details.classList.contains('is-animating')) return;
    animateEpisodeDetails(details, !details.open);
  }

  function handlePlayEpisodeClick(e) {
    const btn = e.target.closest && e.target.closest('[data-play-episode]');
    if (!btn) return;
    e.preventDefault();

    const ctx = {
      audioUrl: btn.dataset.audioUrl,
      episodeNumber: btn.dataset.episodeNumber || null,
      episodeTitle: btn.dataset.episodeTitle || null,
    };

    if (!ctx.audioUrl) {
      console.warn('[podcast] Play button missing audio-url.');
      return;
    }

    openPlayer(ctx, btn);
  }

  function handleDownloadClick(e) {
    const btn = e.target.closest && e.target.closest('[data-download-guide]');
    if (!btn) return;
    e.preventDefault();

    const ctx = {
      pdfUrl: btn.dataset.pdfUrl,
      filename: btn.dataset.filename || 'guide.pdf',
      episodeNumber: btn.dataset.episodeNumber || null,
      episodeTitle: btn.dataset.episodeTitle || null,
    };

    if (!ctx.pdfUrl) {
      console.warn('[podcast] Download button missing pdf-url.');
      return;
    }

    if (isSubscribed() || isInCooldown()) {
      triggerDownload(ctx.pdfUrl, ctx.filename);
      trackKlaviyoEvent('Downloaded Podcast Guide', {
        episode_number: ctx.episodeNumber,
        episode_title: ctx.episodeTitle,
        source: 'direct',
      });
    } else {
      openModal(ctx);
    }
  }

  // ---------- Inline latest-episode player ----------

  function bindInlinePlayer(container) {
    const audio = container.querySelector('[data-inline-audio]');
    const toggle = container.querySelector('[data-inline-toggle]');
    const seek = container.querySelector('[data-inline-seek]');
    const currentTimeEl = container.querySelector('[data-inline-current-time]');
    const durationEl = container.querySelector('[data-inline-duration-time]');
    if (!audio || !toggle) return;

    function syncDuration() {
      const duration = isFinite(audio.duration) ? audio.duration : 0;
      if (duration > 0 && durationEl) durationEl.textContent = formatTime(duration);
      if (seek) seek.max = duration || 100;
    }

    function syncCurrentTime() {
      if (currentTimeEl) currentTimeEl.textContent = formatTime(audio.currentTime);
      if (seek) seek.value = audio.currentTime || 0;
    }

    toggle.addEventListener('click', function () {
      if (audio.paused) {
        audio.play().catch(function (err) {
          console.error('[podcast] Playback failed to start:', err);
        });
      } else {
        audio.pause();
      }
    });

    if (seek) {
      seek.addEventListener('input', function () {
        const nextTime = parseFloat(seek.value);
        if (!isNaN(nextTime)) audio.currentTime = nextTime;
      });
    }

    audio.addEventListener('loadedmetadata', syncDuration);
    audio.addEventListener('durationchange', syncDuration);
    audio.addEventListener('timeupdate', syncCurrentTime);
    audio.addEventListener('play', function () {
      toggle.classList.add('is-playing');
      toggle.setAttribute('aria-pressed', 'true');
      toggle.setAttribute('aria-label', 'Pause episode');
    });
    audio.addEventListener('pause', function () {
      toggle.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      toggle.setAttribute('aria-label', 'Play episode');
    });
    audio.addEventListener('ended', function () {
      toggle.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      toggle.setAttribute('aria-label', 'Play episode');
    });
  }

  function initInlinePlayers() {
    document.querySelectorAll('[data-podcast-inline-player]').forEach(bindInlinePlayer);
  }

  // ---------- Init ----------

  function readConfig(container) {
    if (!container) return;
    config.publicKey = container.dataset.klaviyoPublicKey || null;
    config.listId = container.dataset.klaviyoListId || null;
    const cd = parseInt(container.dataset.promptCooldownDays, 10);
    if (!isNaN(cd) && cd > 0) config.cooldownDays = cd;
  }

  function init() {
    const container = document.querySelector('.podcast-episodes');
    readConfig(container);

    document.querySelectorAll('.podcast-episode__details').forEach(function (details) {
      const summary = details.querySelector('.podcast-episode__summary');
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });

    if (isSubscribed()) {
      // CSS hook so the inline newsletter form (or its empty state)
      // can be hidden for users who already subscribed via the modal.
      document.body.classList.add('podcast-newsletter-subscribed');
    }

    // Delegated click — works for episodes loaded later via section
    // re-render in the theme editor.
    document.addEventListener('click', handleEpisodeDetailsClick);
    document.addEventListener('click', handlePlayEpisodeClick);
    document.addEventListener('click', handleDownloadClick);

    initInlinePlayers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
